import pluralize from 'pluralize';
import picsioConfig from '../../../../../config';
import sendEventToIntercom from '../../services/IntercomEventService';
import Logger from '../../services/Logger';
import notifyBulkExportStartedToSlack from './notifyBulkExportStartedToSlack';

const LIBRARY_EXPORT_THRESHOLD = 0.5;

function getWorkspaceMetadata(user = {}) {
  const team = user.team || {};
  const subscriptionFeatures = user.subscriptionFeatures || {};

  return {
    team: String(team._id || user.teamId || ''),
    planName: subscriptionFeatures.planName,
    userId: String(user._id || ''),
    role: user.role?.name || user.role?.title || user.role?.code || 'owner',
    assetsCount: subscriptionFeatures.assetsCount,
    storageType: team.storageType || user.storageType || (team.picsioStorage ? 'picsio' : 'gd'),
  };
}

function inferInitiatedFrom({ data = {}, config = {} }) {
  if (data.websiteId) return 'website';
  if (data.collectionId || data.downloadFrom || config.collectionId) return 'collection';
  return 'bulk_selection';
}

function resolveExportFormat(data = {}) {
  if (data.isArchive) return 'zip';
  if (data.mimeType && data.mimeType !== 'original') return 'converted';
  if (data.resolution) return 'converted';
  return 'original';
}

function getCollectionsCount(assets = []) {
  const collectionIds = assets.reduce((acc, asset) => {
    (asset.tags || []).forEach((tag) => {
      const id = tag._id || tag.id || tag;
      if (id) acc.add(String(id));
    });
    return acc;
  }, new Set());

  return collectionIds.size;
}

function getAddonEventName(addonName) {
  const addonNameValue = String(addonName || '');
  const countedAddonName = addonNameValue.match(/^\d+\s+(.+)$/);

  return countedAddonName ? pluralize.singular(countedAddonName[1]) : addonNameValue;
}

export function getExportedAssetsCount(assets = []) {
  return assets.length;
}

export function meetsLibraryExportThreshold(exportedCount, workspaceAssetsCount) {
  if (!exportedCount || !workspaceAssetsCount) return false;

  return exportedCount / workspaceAssetsCount >= LIBRARY_EXPORT_THRESHOLD;
}

export function isExcludedChurnExport({ assets = [], data = {}, config = {} }) {
  if (picsioConfig.isProofing) return true;
  if (data.websiteId) return true;
  if (data.resolution) return true;

  const isCollectionExport = Boolean(
    data.collectionId || data.downloadFrom || config.collectionId,
  );

  if (assets.length <= 1 && !isCollectionExport) return true;

  return false;
}

export function isLargeLibraryExport({
  user,
  assets = [],
  data = {},
  config = {},
}) {
  if (isExcludedChurnExport({ assets, data, config })) return false;

  const workspaceAssetsCount = user?.subscriptionFeatures?.assetsCount;
  const exportedCount = getExportedAssetsCount(assets);

  return meetsLibraryExportThreshold(exportedCount, workspaceAssetsCount);
}

export function isChurnRiskExportNotification({
  user,
  assets = [],
  data = {},
  config = {},
}) {
  return isLargeLibraryExport({
    user,
    assets,
    data,
    config,
  });
}

export function isSlackBulkExportNotification(params) {
  return isChurnRiskExportNotification(params);
}

export function trackAddonRemovedEvents(user, removedAddons = []) {
  if (!removedAddons.length) return;

  const metadata = getWorkspaceMetadata(user);

  removedAddons.forEach((addonName) => {
    sendEventToIntercom('addon_removed', {
      ...metadata,
      addon_name: getAddonEventName(addonName),
      removed_at: new Date().toISOString(),
    });
  });
}

/** @deprecated Use isLargeLibraryExport */
export function isBulkExport(params) {
  return isLargeLibraryExport(params);
}

export function trackBulkExportStarted({
  user,
  assets = [],
  data = {},
  config = {},
  initiatedFrom,
}) {
  if (!isChurnRiskExportNotification({
    user,
    assets,
    data,
    config,
  })) return false;

  const metadata = getWorkspaceMetadata(user);
  const collectionId = data.collectionId
    || data.downloadFrom
    || config.collectionId
    || assets.find((asset) => asset.collectionId)?.collectionId;

  sendEventToIntercom('bulk_export_started', {
    ...metadata,
    export_size: assets.reduce((sum, asset) => sum + (Number(asset.fileSize) || 0), 0),
    storage_usage: user?.subscriptionFeatures?.totalSize,
    collections_count: collectionId || data.websiteId ? 1 : getCollectionsCount(assets),
    initiated_from: initiatedFrom || inferInitiatedFrom({ data, config }),
    export_format: resolveExportFormat(data),
    created_at: new Date().toISOString(),
  });
  return true;
}

export async function trackLargeLibraryExportAttempt({
  user,
  assets = [],
  data = {},
  config = {},
  initiatedFrom,
}) {
  if (!isChurnRiskExportNotification({
    user,
    assets,
    data,
    config,
  })) {
    return { intercomTracked: false, slackNotified: false };
  }

  trackBulkExportStarted({
    user,
    assets,
    data,
    config,
    initiatedFrom,
  });

  let slackNotified = false;
  try {
    await notifyBulkExportStartedToSlack({
      user,
      assets,
      data,
      config,
    });
    slackNotified = true;
  } catch (err) {
    Logger.error(new Error('Failed to send bulk export started Slack notification'), { error: err }, [
      'BulkExportStartedSlackNotificationFailed',
      { errorMessage: (err && err.message) || 'NoMessage' },
    ]);
  }

  return { intercomTracked: true, slackNotified };
}
